import { supabase } from './supabase'
import { base } from './db'
import type { Annotation, AnnotationLocale } from './types'

/**
 * Annotations des plans.
 *
 * Tout passe d'abord par la base locale de l'appareil : une annotation
 * créée ou supprimée sans réseau est enregistrée tout de suite sur le
 * téléphone, puis envoyée à la reconnexion par la synchronisation
 * habituelle (voir sync.ts). Le PDF du plan n'est jamais modifié.
 */

const COLONNES =
  'id, document_id, page, auteur_id, type, couleur, epaisseur, geometrie, texte, numero_revision, cree_le, supprime_le'

/** Annotations d'un document, telles que connues sur l'appareil. */
export async function annotationsLocales(documentId: string): Promise<AnnotationLocale[]> {
  const toutes = await base.annotations.where('document_id').equals(documentId).toArray()
  return toutes.filter((a) => a.etat !== 'a_supprimer' && !a.supprime_le)
}

/**
 * Rafraîchit la copie locale d'un document à partir du serveur, sans
 * toucher à ce qui attend encore d'être envoyé. Sans réseau, on garde
 * simplement la copie locale.
 */
export async function rafraichirAnnotations(documentId: string): Promise<AnnotationLocale[]> {
  if (navigator.onLine) {
    const { data, error } = await supabase
      .from('annotations')
      .select(COLONNES)
      .eq('document_id', documentId)
      .is('supprime_le', null)
    if (!error && data) {
      const serveur = new Map((data as Annotation[]).map((a) => [a.id, a]))
      await base.transaction('rw', base.annotations, async () => {
        const locales = await base.annotations.where('document_id').equals(documentId).toArray()
        for (const l of locales) {
          // Supprimée ailleurs (ou par un administrateur) : on la retire ici.
          if (l.etat === 'ok' && !serveur.has(l.id)) await base.annotations.delete(l.id)
        }
        const enAttente = new Set(locales.filter((l) => l.etat !== 'ok').map((l) => l.id))
        await base.annotations.bulkPut(
          [...serveur.values()].filter((a) => !enAttente.has(a.id)).map((a) => ({ ...a, etat: 'ok' as const }))
        )
      })
    }
  }
  return annotationsLocales(documentId)
}

/** Enregistre une nouvelle annotation sur l'appareil ; l'envoi suit. */
export async function creerAnnotation(a: Annotation): Promise<AnnotationLocale> {
  const locale: AnnotationLocale = { ...a, etat: 'a_envoyer', essais: 0 }
  await base.annotations.put(locale)
  return locale
}

/** Supprime une annotation. Celle qui n'a jamais quitté l'appareil disparaît simplement. */
export async function supprimerAnnotation(id: string): Promise<void> {
  const a = await base.annotations.get(id)
  if (!a) return
  if (a.etat === 'a_envoyer') await base.annotations.delete(id)
  else await base.annotations.put({ ...a, etat: 'a_supprimer', essais: 0 })
}

export async function annotationsEnAttente(): Promise<number> {
  return base.annotations.where('etat').anyOf('a_envoyer', 'a_supprimer').count()
}

/**
 * Envoie au serveur ce qui attend. L'identifiant étant fixé sur l'appareil,
 * un envoi retenté après une coupure ne crée jamais de doublon.
 * S'arrête au premier échec, comme pour les photos.
 */
export async function envoyerAnnotations(utilisateurId: string, apresChaque?: () => Promise<void>): Promise<void> {
  const file = await base.annotations.where('etat').anyOf('a_envoyer', 'a_supprimer').sortBy('cree_le')
  for (const a of file) {
    try {
      if (a.etat === 'a_envoyer') {
        const { error } = await supabase.from('annotations').insert({
          id: a.id,
          document_id: a.document_id,
          page: a.page,
          auteur_id: utilisateurId,
          type: a.type,
          couleur: a.couleur,
          epaisseur: a.epaisseur,
          geometrie: a.geometrie,
          texte: a.texte,
          numero_revision: a.numero_revision,
          cree_le: a.cree_le
        })
        // 23505 = déjà passée lors d'un envoi précédent.
        if (error && error.code !== '23505') throw error
        await base.annotations.update(a.id, { etat: 'ok', derniere_erreur: undefined })
      } else {
        const { error } = await supabase
          .from('annotations')
          .update({ supprime_le: new Date().toISOString(), supprime_par: utilisateurId })
          .eq('id', a.id)
        if (error) throw error
        await base.annotations.delete(a.id)
      }
      await apresChaque?.()
    } catch (e) {
      await base.annotations.update(a.id, {
        essais: (a.essais ?? 0) + 1,
        derniere_erreur: e instanceof Error ? e.message : String(e)
      })
      break
    }
  }
}

/** Noms des membres de l'équipe, pour afficher l'auteur d'une annotation. */
let noms: Map<string, string> | null = null
export async function nomsAuteurs(): Promise<Map<string, string>> {
  if (noms) return noms
  try {
    const { data } = await supabase.from('profils').select('id, nom')
    if (data) noms = new Map(data.map((p: { id: string; nom: string }) => [p.id, p.nom]))
  } catch {
    /* hors ligne : les noms s'afficheront plus tard */
  }
  return noms ?? new Map()
}
